import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { Dialog } from '@/_shared/components/Dialog';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';

export const AddEmployeeChoiceDialog = () => {
  const t = useTranslations('people.addEmployee.choiceDialog');
  const navigate = useNavigate();
  const { genericResponsiveValue } = useResponsiveStyle();
  const gapS = convertSizeToNumber(`${useThemeValue('space', 's')}`);

  const { isOpen, closeDialog } = useDialog('addEmployeeChoice');
  const { openDialog: openManualForm } = useDialog('addEmployeeManual');

  const handleManual = useCallback(() => {
    closeDialog();
    openManualForm();
  }, [closeDialog, openManualForm]);

  const handleImport = useCallback(() => {
    closeDialog();
    navigate(Routes.PeopleImport);
  }, [closeDialog, navigate]);

  return (
    <Dialog
      name='addEmployeeChoice'
      open={isOpen}
      onClose={closeDialog}
      closeButton
      maxWidth='900px'
    >
      <Box display='flex' flexDirection='column' width='100%'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
        <Typography variant='p2/light' mt='xs' mb='m' color='primary/text' textAlign='center'>
          {t('subtitle')}
        </Typography>

        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { mobile: 'column' },
            defaultValue: 'row',
          })}
          style={{ gap: gapS }}
        >
          {/* Manual card */}
          <Card
            display='flex'
            flexDirection='column'
            onClick={handleManual}
            style={{ cursor: 'pointer' }}
            p='m'
            backgroundColor='grey/dark/background'
            flex={1}
            data-testid='add-employee-choice-manual'
          >
            <Box mt='none' mb='xxs' display='flex' flexDirection='row' flex={1} alignItems='center'>
              <Svg name='userPlus' size='s' />
              <Typography variant='p1/bold' ml='xxs'>
                {t('manual.label')}
              </Typography>
            </Box>
            <Typography variant='p2/light'>{t('manual.description')}</Typography>
          </Card>

          {/* Import card */}
          <Card
            display='flex'
            flexDirection='column'
            onClick={handleImport}
            style={{ cursor: 'pointer' }}
            p='m'
            backgroundColor='grey/dark/background'
            flex={1}
            data-testid='add-employee-choice-import'
          >
            <Box mt='none' mb='xxs' display='flex' flexDirection='row' flex={1} alignItems='center'>
              <Svg name='file' size='s' />
              <Typography variant='p1/bold' ml='xxs'>
                {t('import.label')}
              </Typography>
            </Box>
            <Typography variant='p2/light'>{t('import.description')}</Typography>
          </Card>
        </Box>
      </Box>
    </Dialog>
  );
};
